import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { getPanelBasePath } from "@/lib/authz";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { AlertTriangle, XCircle, X, ArrowRight } from "lucide-react";

const SEVERITY_CONFIG = {
  error: {
    icon: XCircle,
    bg: "bg-red-50 dark:bg-red-950/30",
    border: "border-red-200 dark:border-red-900",
    icon_color: "text-red-500",
    text: "text-red-700 dark:text-red-400",
  },
  warning: {
    icon: AlertTriangle,
    bg: "bg-amber-50 dark:bg-amber-950/30",
    border: "border-amber-200 dark:border-amber-900",
    icon_color: "text-amber-500",
    text: "text-amber-700 dark:text-amber-400",
  },
};

export default function SmartAlerts({ className }) {
  const [alerts, setAlerts] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem("obihub_dismissed_alerts") || "[]");
    } catch {
      return [];
    }
  });
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api
      .get("/dashboard/smart-alerts")
      .then((data) => setAlerts(Array.isArray(data) ? data : []))
      .catch(() => setAlerts([]));
  }, []);

  const dismiss = (type) => {
    const next = [...dismissed, type];
    setDismissed(next);
    sessionStorage.setItem("obihub_dismissed_alerts", JSON.stringify(next));
  };

  const visible = alerts.filter((a) => !dismissed.includes(a.type));
  if (visible.length === 0) return null;

  const base = getPanelBasePath(user);

  return (
    <div className={cn("space-y-2", className)}>
      {visible.map((alert) => {
        const cfg = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.warning;
        const Icon = cfg.icon;
        return (
          <div
            key={alert.type}
            className={cn(
              "flex items-start gap-3 rounded-lg border px-4 py-3",
              cfg.bg,
              cfg.border
            )}
          >
            <Icon className={cn("h-4 w-4 mt-0.5 shrink-0", cfg.icon_color)} />
            <div className="flex-1 min-w-0">
              <p className={cn("text-sm font-medium", cfg.text)}>{alert.title}</p>
              {alert.desc && (
                <p className="text-xs text-muted-foreground mt-0.5">{alert.desc}</p>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {alert.action_path && (
                <button
                  onClick={() => navigate(`${base}${alert.action_path}`)}
                  className={cn("text-xs font-medium flex items-center gap-0.5 hover:underline", cfg.text)}
                >
                  {alert.action_label}
                  <ArrowRight className="h-3 w-3" />
                </button>
              )}
              <button
                onClick={() => dismiss(alert.type)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
